import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { LazyLoadImageModule } from 'ng-lazyload-image';

import { DataService } from '@/services/data.service';
import { BidTagsService } from '@/services/bid-tags.service';
import { WalletAddressDirective } from '@/directives/wallet-address.directive';
import { TippyDirective } from '@/directives/tippy.directive';
import { LoaderComponent } from '@/components/icons/loader.component';
import { TokenIdParsePipe } from '@/pipes/token-id-parse.pipe';
import { environment } from 'src/environments/environment';
import { normalizeStashFundedForUi } from '@/utils/stash-funded';
import { OnChainBidInfoIconComponent } from '@/components/on-chain-bid-info-icon/on-chain-bid-info-icon.component';
import {
  cloneBidLabel,
  nativeBidTypeLabel,
  onChainBidDetailsLoadError,
  onChainBidTypeLabel,
} from '@/utils/native-market-bid-labels';
import { PunksmarketIndexerBid } from '@/models/unified-market-bid';
import {
  classifyPunksmarketIndexerBid,
  formatPunksmarketBidCriteria,
  PunksmarketBidTargetKind,
} from '@/utils/punksmarket-criteria.util';
import { formatEther } from 'viem';

export interface StashBid {
  id: string;
  bidder_address: string;
  bid_amount_eth: number;
  expires_at?: string;
  [key: string]: any;
}

@Component({
  selector: 'app-bid-details-modal',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    LazyLoadImageModule,
    WalletAddressDirective,
    TippyDirective,
    LoaderComponent,
    TokenIdParsePipe,
    OnChainBidInfoIconComponent,
  ],
  templateUrl: './bid-details-modal.component.html',
  styleUrls: ['./bid-details-modal.component.scss']
})
export class BidDetailsModalComponent implements OnInit, OnChanges {
  readonly nativeBidTypeLabel = nativeBidTypeLabel;
  readonly onChainBidTypeLabel = onChainBidTypeLabel;
  readonly cloneBidLabel = cloneBidLabel;
  @Input() isActive: boolean = false;
  @Input() bid: StashBid | null = null;
  @Output() closed = new EventEmitter<void>();
  @Output() copyBid = new EventEmitter<any>();

  loadingBidDetails: boolean = false;
  bidDetailsError: string | null = null;
  selectedBidPunkIds: number[] = [];
  /** Punks to show in the grid (first 14 or all when "View all" is expanded). */
  displayedPunkIds: number[] = [];
  viewAllPunks: boolean = false;
  bidLinkCopied: boolean = false;
  private readonly PUNKS_PREVIEW_LIMIT = 14;
  private bidLinkCopyTimeout: ReturnType<typeof setTimeout> | null = null;

  /** PunksMarket on-chain criteria lines (traits, colors, allowlists). */
  punksmarketCriteriaLines: string[] = [];
  punksmarketColorRefs: Array<{ id: number; hex: string }> = [];
  punksmarketTargetKind: PunksmarketBidTargetKind | null = null;
  punksmarketSettlementEth: number | null = null;
  punksmarketTxHash: string | null = null;

  protected readonly environment = environment;

  get isPunksmarketBid(): boolean {
    if (!this.bid) {
      return false;
    }
    return (
      (this.bid as any).platform === 'punksmarket' ||
      (this.bid as any).bidType === 'punksmarket' ||
      !!(this.bid as any).punksmarketBidId ||
      String(this.bid.id).startsWith('pm-')
    );
  }

  constructor(
    public dataSvc: DataService,
    private bidTagsService: BidTagsService,
  ) {}

  ngOnInit(): void {
    if (this.isActive && this.bid) {
      this.loadBidDetails();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['bid']?.currentValue) {
      this.normalizeStashFundedOnBid();
    }
    if (changes['isActive'] && changes['isActive'].currentValue && this.bid) {
      this.loadBidDetails();
    }
    if (changes['isActive'] && !changes['isActive'].currentValue) {
      this.resetState();
    }
  }

  private normalizeStashFundedOnBid(): void {
    if (!this.bid) return;
    (this.bid as any).stash_funded = normalizeStashFundedForUi((this.bid as any).stash_funded);
  }

  loadBidDetails(): void {
    if (!this.bid) return;

    this.enrichBidType();
    this.normalizeStashFundedOnBid();

    this.loadingBidDetails = true;
    this.bidDetailsError = null;
    this.selectedBidPunkIds = [];

    const pmBidId =
      (this.bid as any).punksmarketBidId ??
      (String(this.bid.id).startsWith('pm-')
        ? String(this.bid.id).replace(/^pm-/, '')
        : null);

    if (this.isPunksmarketBid) {
      const cached = (this.bid as any).punksmarketIndexerBid as
        | PunksmarketIndexerBid
        | undefined;
      if (cached?.bidId) {
        this.applyPunksmarketIndexerBid(cached);
        this.loadingBidDetails = false;
        return;
      }
      if (pmBidId) {
        this.loadPunksmarketBidDetails(pmBidId);
        return;
      }
      this.loadingBidDetails = false;
      this.selectedBidPunkIds = this.getBidPunkIds(this.bid);
      this.updateDisplayedPunkIds();
      return;
    }

    // Fetch bid details with parsed punk indices
    this.dataSvc.fetchBidDetailsWithPunkIndices(this.bid.id).subscribe({
      next: (bidDetails: any) => {
        this.loadingBidDetails = false;
        if (this.bid && bidDetails && typeof bidDetails === 'object' && 'stash_funded' in bidDetails) {
          (this.bid as any).stash_funded = normalizeStashFundedForUi(bidDetails.stash_funded);
        }
        if (bidDetails && bidDetails.parsed_punk_indices) {
          if (bidDetails.parsed_punk_indices === 'all') {
            // If it's "all", we can't display all punks, so show empty or a message
            this.selectedBidPunkIds = [];
          } else if (Array.isArray(bidDetails.parsed_punk_indices)) {
            this.selectedBidPunkIds = bidDetails.parsed_punk_indices;
          } else {
            // Fallback to existing method
            this.selectedBidPunkIds = this.getBidPunkIds(this.bid!);
          }
        } else {
          // Fallback to existing method if no parsed_punk_indices
          this.selectedBidPunkIds = this.getBidPunkIds(this.bid!);
        }
        this.updateDisplayedPunkIds();
      },
      error: (error) => {
        console.error('Error loading bid details:', error);
        this.loadingBidDetails = false;
        this.bidDetailsError = 'Failed to load bid details';
        // Fallback to existing method
        this.selectedBidPunkIds = this.getBidPunkIds(this.bid!);
        this.updateDisplayedPunkIds();
      }
    });
  }

  getBidPunkIds(bid: StashBid): number[] {
    if ((bid as any).punkIds) {
      return (bid as any).punkIds;
    }
    
    // Fallback: extract from bid directly
    let punkIds: number[] = [];
    try {
      if ((bid as any).selected_punk_ids) {
        punkIds = Array.isArray((bid as any).selected_punk_ids)
          ? (bid as any).selected_punk_ids
          : typeof (bid as any).selected_punk_ids === 'string'
          ? (bid as any).selected_punk_ids.split(',').map((id: string) => parseInt(id.trim())).filter((id: number) => !isNaN(id))
          : [];
      } else if ((bid as any).punkIndices) {
        punkIds = Array.isArray((bid as any).punkIndices) ? (bid as any).punkIndices : [];
      } else if ((bid as any).punk_indices) {
        if (typeof (bid as any).punk_indices === 'string') {
          punkIds = JSON.parse((bid as any).punk_indices);
        } else if (Array.isArray((bid as any).punk_indices)) {
          punkIds = (bid as any).punk_indices;
        }
      }
    } catch (error) {
      console.error('Error parsing punk indices:', error);
    }
    return punkIds;
  }

  private loadPunksmarketBidDetails(pmBidId: string): void {
    const cached = (this.bid as any).punksmarketIndexerBid as
      | PunksmarketIndexerBid
      | undefined;
    if (cached?.bidId) {
      this.applyPunksmarketIndexerBid(cached);
      this.loadingBidDetails = false;
      return;
    }

    this.dataSvc.fetchPunksmarketBidById(pmBidId).subscribe({
      next: (item) => {
        this.loadingBidDetails = false;
        if (!item) {
          this.bidDetailsError = onChainBidDetailsLoadError;
          this.selectedBidPunkIds = this.getBidPunkIds(this.bid!);
          this.updateDisplayedPunkIds();
          return;
        }
        (this.bid as any).punksmarketIndexerBid = item;
        this.applyPunksmarketIndexerBid(item);
      },
      error: (err) => {
        console.error('Error loading PunksMarket bid:', err);
        this.loadingBidDetails = false;
        this.bidDetailsError = onChainBidDetailsLoadError;
        this.selectedBidPunkIds = this.getBidPunkIds(this.bid!);
        this.updateDisplayedPunkIds();
      },
    });
  }

  private applyPunksmarketIndexerBid(item: PunksmarketIndexerBid): void {
    if (!this.bid) {
      return;
    }
    const display = formatPunksmarketBidCriteria(item);
    const { bidType, bidTypeLabel } = classifyPunksmarketIndexerBid(item);
    this.punksmarketCriteriaLines = display.lines;
    this.punksmarketColorRefs = display.colorRefs;
    this.punksmarketTargetKind = display.kind;
    this.bid.decodedTags = display.tags;
    this.bid.biddingOn = display.summary;
    this.bid.bidType = bidType;
    this.bid.bidTypeLabel = bidTypeLabel;

    try {
      const settlementWei = BigInt(item.settlementWei ?? '0');
      this.punksmarketSettlementEth =
        settlementWei > 0n ? Number(formatEther(settlementWei)) : null;
    } catch {
      this.punksmarketSettlementEth = null;
    }
    this.punksmarketTxHash = item.txHash ?? null;

    const includeIds = item.includeIds ?? [];
    this.selectedBidPunkIds = includeIds.length
      ? [...includeIds]
      : this.getBidPunkIds(this.bid);
    this.updateDisplayedPunkIds();
    this.bidDetailsError = null;
  }

  private enrichBidType(): void {
    if (!this.bid) return;
    if (this.isPunksmarketBid) {
      const cached = (this.bid as any).punksmarketIndexerBid as
        | PunksmarketIndexerBid
        | undefined;
      if (cached?.bidId) {
        const { bidType, bidTypeLabel, display } = classifyPunksmarketIndexerBid(cached);
        this.bid.bidType = bidType;
        this.bid.bidTypeLabel = bidTypeLabel;
        this.bid.decodedTags = display.tags;
        return;
      }
      if (
        this.bid.bidType &&
        this.bid.bidType !== 'punksmarket' &&
        this.bid.bidTypeLabel
      ) {
        return;
      }
      if (!this.bid.bidTypeLabel) {
        this.bid.bidTypeLabel = onChainBidTypeLabel;
      }
      return;
    }
    if (this.bid.bidType) return;

    const decodedTags = this.bidTagsService.decodeCollectionBidTags(this.bid);
    const bidType = this.bidTagsService.getCollectionBidType(this.bid, decodedTags);

    this.bid.decodedTags = decodedTags;
    this.bid.bidType = bidType;
    this.bid.bidTypeLabel =
      bidType === 'owner' ? 'Owner Bid' :
      bidType === 'custom' ? 'Custom Bid' :
      bidType === 'trait' ? 'Trait Bid' :
      'Collection Bid';

    if (bidType === 'owner' && decodedTags.length > 0) {
      const match = decodedTags[0].match(/^Owner:\s*(0x[a-fA-F0-9]{40})$/i);
      if (match) {
        this.bid.ownerAddress = match[1].toLowerCase();
      }
    }
    this.normalizeStashFundedOnBid();
  }

  closeModal(): void {
    this.closed.emit();
    this.resetState();
  }

  onCopyBid(): void {
    if (this.bid) {
      this.copyBid.emit(this.bid);
      this.closeModal();
    }
  }

  onCopyBidDetailLink(event: Event): void {
    event.stopPropagation();
    if (!this.bid?.id || typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
      return;
    }
    const base = environment.marketplaceUrl.replace(/\/$/, '');
    const url = `${base}/bids/detail/${encodeURIComponent(this.bid.id)}`;
    navigator.clipboard.writeText(url).then(() => {
      this.bidLinkCopied = true;
      if (this.bidLinkCopyTimeout != null) {
        clearTimeout(this.bidLinkCopyTimeout);
      }
      this.bidLinkCopyTimeout = setTimeout(() => {
        this.bidLinkCopied = false;
        this.bidLinkCopyTimeout = null;
      }, 1500);
    }).catch((err) => console.error('Failed to copy bid link', err));
  }

  toggleViewAllPunks(): void {
    this.viewAllPunks = !this.viewAllPunks;
    this.updateDisplayedPunkIds();
  }

  private updateDisplayedPunkIds(): void {
    if (this.viewAllPunks) {
      this.displayedPunkIds = [...this.selectedBidPunkIds];
    } else {
      this.displayedPunkIds = this.selectedBidPunkIds.slice(0, this.PUNKS_PREVIEW_LIMIT);
    }
  }

  private resetState(): void {
    this.loadingBidDetails = false;
    this.bidDetailsError = null;
    this.selectedBidPunkIds = [];
    this.displayedPunkIds = [];
    this.viewAllPunks = false;
    this.punksmarketCriteriaLines = [];
    this.punksmarketColorRefs = [];
    this.punksmarketTargetKind = null;
    this.punksmarketSettlementEth = null;
    this.punksmarketTxHash = null;
    if (this.bidLinkCopyTimeout != null) {
      clearTimeout(this.bidLinkCopyTimeout);
      this.bidLinkCopyTimeout = null;
    }
    this.bidLinkCopied = false;
  }
}
